import { Anchor, Badge, Group, Stack, Text } from '@mantine/core';
import {
  ASSET_SET_DECIDED_VERB,
  ASSET_SET_LIVE_LABEL,
  ASSET_SET_STATE_COLORS,
  ASSET_SET_STATE_LABELS,
  BRAND_KIT_COPY,
} from '@/lib/constants/appFactory';
import type { FactoryAssetSet } from '@/lib/types/AppFactory';
import { AppFactoryUtils } from '../AppFactoryUtils';
import { BrandKitUtils } from '../BrandKitUtils';

interface AssetSetHistoryProps {
  assetSets: FactoryAssetSet[];
  /** Operators also read who approved past the gate. */
  showOverrideActor?: boolean;
}

/**
 * One platform's decided sets, newest decision first: a set made earlier can
 * be approved later, so creation order does not say which is live (3.36.1).
 * A set an operator approved past a blocked quality gate says so, with the
 * reason (3.42.0).
 */
export function AssetSetHistory({ assetSets, showOverrideActor }: AssetSetHistoryProps) {
  const history = BrandKitUtils.historyRows(assetSets);
  if (history.length === 0) return null;
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        History
      </Text>
      {history.map(({ set: s, live }) => (
        <Stack key={s.assetSetId} gap={0}>
          <Group gap="xs">
            <Badge variant={live ? 'filled' : 'light'} color={ASSET_SET_STATE_COLORS[s.state] ?? 'gray'} size="sm">
              {live ? ASSET_SET_LIVE_LABEL : (ASSET_SET_STATE_LABELS[s.state] ?? s.state)}
            </Badge>
            <Text size="xs" c="dimmed">
              {s.source === 'manual' ? 'Uploaded' : 'Generated'} {AppFactoryUtils.formatWhen(s.created)}
              {ASSET_SET_DECIDED_VERB[s.state] && s.updated !== s.created
                ? ` · ${ASSET_SET_DECIDED_VERB[s.state]} ${AppFactoryUtils.formatWhen(s.updated)}`
                : ''}
            </Text>
            {s.proofSheetUrl && (
              <Anchor href={s.proofSheetUrl} target="_blank" rel="noopener noreferrer" size="xs">
                preview
              </Anchor>
            )}
          </Group>
          {s.override && (
            <Text size="xs" c="orange" data-testid="set-override">
              {BRAND_KIT_COPY.overrideBadge}
              {showOverrideActor && s.override.actor ? ` (${s.override.actor})` : ''}: {s.override.reason}
            </Text>
          )}
        </Stack>
      ))}
    </Stack>
  );
}
